import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../Header';
import Footer from '../Footer';
import { getMaterialBySlug, getOrgById } from './libraryData';
import { useAdmin } from '../../contexts/AdminContext';
import EditableText from '../admin/EditableText';
import SourcesEditor from '../admin/SourcesEditor';
import TagsEditor from '../admin/TagsEditor';
import './Library.css';

function slugify(text) {
  return text.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function MaterialDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editedData, setEditedData] = useState({});
  const [showSourcesEditor, setShowSourcesEditor] = useState(false);
  const [showTagsEditor, setShowTagsEditor] = useState(false);
  const [showTacticsEditor, setShowTacticsEditor] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/materials/${slug}`);
        if (res.ok) {
          const data = await res.json();
          // Parse JSON string fields
          if (typeof data.tags === 'string') data.tags = JSON.parse(data.tags);
          if (typeof data.tactics === 'string') data.tactics = JSON.parse(data.tactics);
          if (typeof data.sources === 'string') data.sources = JSON.parse(data.sources);
          if (!cancelled) setApiData(data);
        }
      } catch (e) {
        console.error('Failed to fetch from API:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [slug]);

  // Use API data if available, fall back to static
  const staticData = getMaterialBySlug(slug);
  const material = apiData || staticData;

  const saveToAPI = async (field, value) => {
    try {
      const response = await fetch(`/api/materials/${material.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      if (!response.ok) {
        const err = await response.json();
        console.error('Save failed:', err);
        alert('Save failed: ' + (err.error || 'Unknown error'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      alert('Save failed: network error');
      return false;
    }
  };

  const handleFieldSave = async (field, value) => {
    if (field === 'title') {
      const newSlug = slugify(value);
      try {
        const response = await fetch(`/api/materials/${material.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title: value, slug: newSlug }),
        });
        if (response.ok) {
          setEditedData(prev => ({ ...prev, title: value, slug: newSlug }));
          if (newSlug !== slug) navigate(`/materials/${newSlug}`, { replace: true });
        } else {
          const err = await response.json();
          alert('Save failed: ' + (err.error || 'Unknown error'));
        }
      } catch (error) {
        alert('Save failed: network error');
      }
      return;
    }
    const ok = await saveToAPI(field, value);
    if (ok) setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleSourcesSave = async (sources) => {
    const ok = await saveToAPI('sources', sources);
    if (ok) setEditedData(prev => ({ ...prev, sources }));
    setShowSourcesEditor(false);
  };

  const handleTagsSave = async (tags) => {
    const ok = await saveToAPI('tags', tags);
    if (ok) setEditedData(prev => ({ ...prev, tags }));
    setShowTagsEditor(false);
  };

  const handleTacticsSave = async (tactics) => {
    const ok = await saveToAPI('tactics', tactics);
    if (ok) setEditedData(prev => ({ ...prev, tactics }));
    setShowTacticsEditor(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className='detail-page-container'>
          <div className='detail-content'>
            <p>Loading...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!material) {
    return (
      <>
        <Header />
        <main className='detail-page-container'>
          <div className='detail-content'>
            <h1>Material not found</h1>
            <p>The material you are looking for does not exist.</p>
            <Link to='/library'>Back to Library</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const org = material.orgId ? getOrgById(material.orgId) : null;
  const pageTitle = `${material.title} - Propaganda Library - Hasbara Tracker`;
  const pageDescription = material.description.substring(0, 160);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name='description' content={pageDescription} />
        <meta property='og:title' content={material.title} />
        <meta property='og:description' content={pageDescription} />
        <meta property='og:type' content='article' />
      </Helmet>

      <Header />

      <main className='detail-page-container'>
        {/* Breadcrumb */}
        <nav className='breadcrumb' aria-label='Breadcrumb'>
          <Link to='/library'>Library</Link>
          <span>/</span>
          <Link to='/library' state={{ tab: 'materials' }}>Materials</Link>
          <span>/</span>
          <span>{material.title}</span>
        </nav>

        {/* Title */}
        <h1 className='detail-title'>
          {isAdmin ? (
            <EditableText
              value={editedData.title !== undefined ? editedData.title : material.title}
              onSave={(val) => handleFieldSave('title', val)}
              tag='span'
            />
          ) : (
            material.title
          )}
        </h1>

        {/* Two-column layout */}
        <div className='detail-layout'>
          {/* Main column */}
          <div className='detail-main'>
            {/* Document viewer */}
            <div className='document-viewer'>
              {material.hostedUrl ? (
                <iframe
                  src={`/viewer.html?file=${encodeURIComponent(material.hostedUrl)}`}
                  title={material.title}
                  className='document-iframe'
                  allow='fullscreen'
                />
              ) : material.externalUrl ? (
                <div className='document-external'>
                  <div className='external-material-card'>
                    <span className='external-badge'>EXTERNAL SOURCE</span>
                    <p className='external-description'>
                      This material is hosted externally and has not yet been archived on this site.
                    </p>
                    <a
                      href={material.externalUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='external-view-link'
                    >
                      View material at source →
                    </a>
                  </div>
                </div>
              ) : (
                <div className='document-placeholder'>
                  <p>Material not yet archived on this site.</p>
                  {material.sources && material.sources.length > 0 && (
                    <div className='placeholder-links'>
                      <p>View from original sources:</p>
                      <ul>
                        {material.sources.map((source, idx) => (
                          <li key={idx}>
                            <a href={source.url} target='_blank' rel='noopener noreferrer'>
                              {source.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Viewer toolbar */}
            {material.hostedUrl && (
              <div className='viewer-toolbar'>
                <span className='toolbar-title'>{material.title}</span>
                <a
                  href={material.hostedUrl}
                  download
                  className='download-button'
                >
                  Download PDF
                </a>
              </div>
            )}

            {/* Description */}
            <section className='detail-description'>
              <p className='material-description'>
                {isAdmin ? (
                  <EditableText
                    value={editedData.description !== undefined ? editedData.description : material.description}
                    onSave={(val) => handleFieldSave('description', val)}
                    tag='span'
                    multiline={true}
                  />
                ) : (
                  material.description
                )}
              </p>
            </section>
          </div>

          {/* Sidebar */}
          <aside className='detail-sidebar'>
            <dl className='detail-meta-list'>
              <dt>Type</dt>
              <dd>{material.type.toUpperCase()}</dd>

              {material.date && (
                <>
                  <dt>Date</dt>
                  <dd>{material.date}</dd>
                </>
              )}

              {material.pages && (
                <>
                  <dt>Pages</dt>
                  <dd>{material.pages}</dd>
                </>
              )}

              {org && (
                <>
                  <dt>Produced by</dt>
                  <dd>
                    <Link to={`/organisations/${org.slug}`}>{org.name}</Link>
                  </dd>
                </>
              )}

              {(material.tags && material.tags.length > 0) || isAdmin ? (
                <>
                  <dt style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <span>Content type</span>
                    {isAdmin && (
                      <button
                        className='admin-btn-small'
                        onClick={() => setShowTagsEditor(true)}
                        title='Edit tags'
                        style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                      >
                        Edit
                      </button>
                    )}
                  </dt>
                  <dd className='detail-tag-list'>
                    {(editedData.tags !== undefined ? editedData.tags : material.tags || []).map((tag, idx) => (
                      <span key={idx} className='detail-tag'>{tag}</span>
                    ))}
                  </dd>
                </>
              ) : null}

              {(material.tactics && material.tactics.length > 0) || isAdmin ? (
                <>
                  <dt style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <span>Tactics</span>
                    {isAdmin && (
                      <button
                        className='admin-btn-small'
                        onClick={() => setShowTacticsEditor(true)}
                        title='Edit tactics'
                        style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                      >
                        Edit
                      </button>
                    )}
                  </dt>
                  <dd className='detail-tag-list'>
                    {(editedData.tactics !== undefined ? editedData.tactics : material.tactics || []).map((tag, idx) => (
                      <span key={idx} className='detail-tag'>{tag}</span>
                    ))}
                  </dd>
                </>
              ) : null}
            </dl>

            {/* Sources */}
            {(material.sources && material.sources.length > 0) || isAdmin ? (
              <div className='detail-sidebar-section'>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3 className='detail-sidebar-heading'>Sources</h3>
                  {isAdmin && (
                    <button
                      className='admin-btn-small'
                      onClick={() => setShowSourcesEditor(true)}
                      title='Edit sources'
                      style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <ul className='detail-sidebar-links'>
                  {(editedData.sources !== undefined ? editedData.sources : material.sources || []).map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target='_blank' rel='noopener noreferrer'>
                        {source.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {showSourcesEditor && (
              <SourcesEditor
                sources={editedData.sources !== undefined ? editedData.sources : material.sources || []}
                onSave={handleSourcesSave}
                onClose={() => setShowSourcesEditor(false)}
              />
            )}
            {showTagsEditor && (
              <TagsEditor
                title='Edit Content Types'
                selected={editedData.tags !== undefined ? editedData.tags : material.tags || []}
                options={['Article', 'Report', 'Statement', 'Analysis', 'Opinion', 'Fact-check', 'Investigation']}
                onSave={handleTagsSave}
                onClose={() => setShowTagsEditor(false)}
              />
            )}
            {showTacticsEditor && (
              <TagsEditor
                title='Edit Tactics'
                selected={editedData.tactics !== undefined ? editedData.tactics : material.tactics || []}
                options={['Disinformation', 'Propaganda', 'Harassment', 'Delegitimization', 'Denial', 'Whitewashing']}
                onSave={handleTacticsSave}
                onClose={() => setShowTacticsEditor(false)}
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </>
  );
}
